import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Header } from '../components/layout/Header';
import { BottomNav } from '../components/layout/BottomNav';
import { GigCard, type GigItem } from '../components/gigs/GigCard';
import { ApplyGigModal, type ApplyGigData } from '../components/gigs/ApplyGigModal';
import { useGetGigs, useGetPublicGigs, type GigFilterParams } from '../api/queries';
import { useAuth } from '../hooks/useAuth';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { isGigActive } from '../utils/gigDate';
import { extractApiError } from '../utils/apiError';
import {
  FiSearch,
  FiMapPin,
  FiCalendar,
  FiChevronDown,
  FiBriefcase,
  FiX,
  FiPlus,
} from 'react-icons/fi';
import { DateInput } from '../components/ui/DateInput';

export const Route = createFileRoute('/gigs/')({
  component: GigDiscovery,
});

const PAGE_SIZE = 8;

interface FilterDropdownProps {
  label: string;
  icon: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

function FilterDropdown({ label, icon, isOpen, onToggle, children }: FilterDropdownProps) {
  return (
    <div className="relative">
      <button
        onClick={onToggle}
        className={`flex items-center gap-2 h-10 px-4 rounded-full border text-[13px] font-medium transition-all ${
          isOpen
            ? 'bg-white/10 border-white/20 text-white'
            : 'bg-[#1a1a1a] border-white/10 text-zinc-300 hover:bg-white/5'
        }`}
      >
        {icon}
        <span>{label}</span>
        <FiChevronDown size={13} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 min-w-52 max-w-[90vw] bg-[#1a1a1a] border border-white/10 rounded-2xl overflow-hidden shadow-2xl shadow-black/50 z-30">
          {children}
        </div>
      )}
    </div>
  );
}

function GigDiscovery() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  // Filter state
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [city, setCity] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [allGigs, setAllGigs] = useState<GigItem[]>([]);
  const [applyGig, setApplyGig] = useState<GigItem | null>(null);

  // Dropdown open states
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset pagination when filters change
  const handleFilterChange = useCallback(() => {
    setPage(1);
  }, []);

  // Date filter validation: end date cannot be before start date
  const dateError = useMemo(() => {
    if (startDate && endDate && endDate < startDate) {
      return 'End date cannot be before start date';
    }
    return null;
  }, [startDate, endDate]);

  // Date params are intentionally excluded from API query — filtering is done client-side.
  const queryParams: GigFilterParams = useMemo(
    () => ({
      ...(debouncedSearch && { search: debouncedSearch }),
      ...(city && { city }),
      page,
      page_size: PAGE_SIZE,
    }),
    [debouncedSearch, city, page],
  );

  const querySignature = useMemo(() => {
    const qs = new URLSearchParams();
    Object.entries(queryParams).forEach(([key, val]) => {
      if (val !== undefined && val !== '' && val !== null) {
        qs.set(key, String(val));
      }
    });
    return qs.toString();
  }, [queryParams]);

  // Always call both hooks; only one fetches based on auth state
  const authGigsResult = useGetGigs(queryParams, { enabled: isAuthenticated });
  const publicGigsResult = useGetPublicGigs(queryParams, { enabled: !isAuthenticated });
  const { data, isLoading, isFetching, isError, error, refetch } = isAuthenticated
    ? authGigsResult
    : publicGigsResult;

  // Stable key representing the current filter set (everything except page)
  const filterKey = useMemo(
    () =>
      JSON.stringify({
        debouncedSearch,
        city,
      }),
    [debouncedSearch, city],
  );

  // Accumulate gigs: replace on page 1, append on page 2+
  useEffect(() => {
    if (!data?.results) return;
    if (data.requestSignature !== querySignature) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const normalised: GigItem[] = (data.results as any[]).map(
      (g): GigItem => ({
        ...g,
        id: String(g.id ?? g.gig_uuid ?? ''),
        timezone: (g.timezone as string) ?? undefined,
        owner_id: (g.owner_id ?? g.owner?.id ?? g.user_id ?? g.user?.id ?? g.created_by) as
          | string
          | number,
      }),
    );

    if (queryParams.page === 1) {
      setAllGigs(normalised);
    } else {
      setAllGigs((prev) => [...prev, ...normalised]);
    }
  }, [data, queryParams.page, querySignature]);

  // When filters change, wipe accumulated gigs so old results don't linger
  useEffect(() => {
    setAllGigs([]);
  }, [filterKey]);

  const hasMore = data?.next != null;

  const closeAllDropdowns = () => setOpenDropdown(null);

  // ── Client-side date filter + sort (active first) ────────────
  const displayedGigs = useMemo(() => {
    // Parse filter boundaries once
    const filterStart = startDate ? new Date(`${startDate}T00:00:00`) : null;
    // Only apply end date if it's valid (not before start)
    const filterEnd =
      endDate && (!startDate || endDate >= startDate) ? new Date(`${endDate}T23:59:59`) : null;

    return [...allGigs]
      .filter((gig) => {
        if (!filterStart && !filterEnd) return true;
        // Match against start_datetime; fall back to end_datetime if absent
        const gigDateStr = gig.start_datetime || gig.end_datetime;
        if (!gigDateStr) return true; // no date info — keep the gig visible
        const gigDate = new Date(gigDateStr);
        if (Number.isNaN(gigDate.getTime())) return true;
        if (filterStart && gigDate < filterStart) return false;
        if (filterEnd && gigDate > filterEnd) return false;
        return true;
      })
      .sort((a, b) => {
        const aActive = isGigActive(a) ? 1 : 0;
        const bActive = isGigActive(b) ? 1 : 0;
        return bActive - aActive;
      });
  }, [allGigs, startDate, endDate]);

  const activeFilterCount = [
    city,
    startDate && !dateError ? startDate : '',
    endDate && !dateError ? endDate : '',
  ].filter(Boolean).length;

  return (
    <div
      className="min-h-screen bg-black text-white pb-32"
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('.filter-dropdown') === null) {
          closeAllDropdowns();
        }
      }}
    >
      <Header />

      <main className="pt-24 md:pt-20">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 pt-6">
            <div>
              <h1 className="text-white text-2xl sm:text-3xl font-bold">Gig Discovery</h1>
              <p className="text-zinc-500 text-[14px] mt-1">Find the perfect opportunity</p>
            </div>
            {isAuthenticated && (
              <Link
                to="/gigs/create"
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-[14px] font-semibold px-5 py-2.5 rounded-full transition-all active:scale-95 self-start sm:self-auto"
              >
                <FiPlus size={16} />
                Post a Gig
              </Link>
            )}
          </div>

          {/* Search bar */}
          {isAuthenticated && (
            <>
              <div className="relative flex items-center bg-[#111111] border border-white/10 rounded-full px-5 h-13 mb-5">
                <FiSearch size={18} className="text-zinc-500 shrink-0" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search a gig..."
                  className="ml-3 bg-transparent border-none text-white text-[15px] placeholder:text-zinc-600 focus:ring-0 focus:outline-none w-full"
                />
                {search && (
                  <button
                    onClick={() => {
                      setSearch('');
                      handleFilterChange();
                    }}
                    className="text-zinc-500 hover:text-white transition-colors"
                  >
                    <FiX size={16} />
                  </button>
                )}
                <button className="ml-3 w-9 h-9 rounded-full bg-blue-600 hover:bg-blue-500 flex items-center justify-center shrink-0 transition-all active:scale-95">
                  <FiSearch size={15} className="text-white" />
                </button>
              </div>

              {/* Filters row */}
              <div className="flex items-center gap-2 flex-wrap mb-8 filter-dropdown">
                {/* City */}
                <div className="relative flex items-center bg-[#111111] border border-white/10 rounded-full px-4 h-10">
                  <FiMapPin size={16} className="text-zinc-500 shrink-0" />
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => {
                      setCity(e.target.value);
                      handleFilterChange();
                    }}
                    placeholder="Search by city..."
                    className="ml-3 bg-transparent border-none text-white text-[14px] placeholder:text-zinc-600 focus:ring-0 focus:outline-none w-full"
                  />
                  {city && (
                    <button
                      onClick={() => {
                        setCity('');
                        handleFilterChange();
                      }}
                      className="text-zinc-500 hover:text-white transition-colors ml-2 shrink-0"
                    >
                      <FiX size={16} />
                    </button>
                  )}
                </div>

                {/* Date */}
                <FilterDropdown
                  label={startDate ? `From ${startDate}` : 'Date'}
                  icon={<FiCalendar size={14} />}
                  isOpen={openDropdown === 'date'}
                  onToggle={() => setOpenDropdown(openDropdown === 'date' ? null : 'date')}
                >
                  <div className="p-4 flex flex-col gap-3">
                    <div className="flex flex-col gap-1">
                      <label className="text-zinc-500 text-[11px] uppercase tracking-wider">
                        Start date
                      </label>
                      <DateInput
                        value={startDate}
                        onChange={(val) => {
                          setStartDate(val);
                          handleFilterChange();
                        }}
                        placeholder="Start date"
                        className="h-9 border-white/10 rounded-xl px-3 text-[13px] bg-black/40"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-zinc-500 text-[11px] uppercase tracking-wider">
                        End date
                      </label>
                      <DateInput
                        value={endDate}
                        onChange={(val) => {
                          setEndDate(val);
                          handleFilterChange();
                        }}
                        placeholder="End date"
                        className={`h-9 border rounded-xl px-3 text-[13px] bg-black/40 ${
                          dateError ? 'border-red-500/60' : 'border-white/10'
                        }`}
                      />
                      {dateError && <p className="text-red-400 text-[11px] mt-0.5">{dateError}</p>}
                    </div>
                    {(startDate || endDate) && (
                      <button
                        className="text-red-400 text-[12px] hover:text-red-300 transition-colors"
                        onClick={() => {
                          setStartDate('');
                          setEndDate('');
                          handleFilterChange();
                          closeAllDropdowns();
                        }}
                      >
                        Clear dates
                      </button>
                    )}
                  </div>
                </FilterDropdown>

                {/* Clear all */}
                {activeFilterCount > 0 && (
                  <button
                    className="flex items-center gap-1.5 text-[12px] text-zinc-400 hover:text-red-400 transition-colors ml-1"
                    onClick={() => {
                      setCity('');
                      setSearch('');
                      setStartDate('');
                      setEndDate('');
                      handleFilterChange();
                    }}
                  >
                    <FiX size={13} />
                    Clear all ({activeFilterCount})
                  </button>
                )}
              </div>
            </>
          )}

          {/* Results count */}
          {!isLoading && displayedGigs.length > 0 && (
            <p className="text-zinc-500 text-[13px] mb-6">
              {displayedGigs.length} gig{displayedGigs.length !== 1 ? 's' : ''} found
            </p>
          )}

          {/* Loading skeleton */}
          {isLoading && allGigs.length === 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-[18px] border border-white/10 bg-[#0d0d0d] animate-pulse overflow-hidden"
                >
                  <div className="w-full h-58 bg-zinc-800" />
                  <div className="p-4 flex flex-col gap-3">
                    <div className="h-3 bg-zinc-800 rounded w-2/5" />
                    <div className="h-4 bg-zinc-800 rounded w-3/4" />
                    <div className="h-3 bg-zinc-800 rounded w-full" />
                    <div className="h-3 bg-zinc-800 rounded w-4/5" />
                    <div className="h-3 bg-zinc-800 rounded w-1/3 mt-1" />
                    <div className="h-10 bg-zinc-800 rounded-full mt-1" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Error state: only when nothing is cached to show */}
          {isError && !isFetching && displayedGigs.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <h3 className="text-white text-xl font-semibold mb-2">Couldn't load gigs</h3>
              <p className="text-zinc-500 text-[15px] max-w-xs">
                {extractApiError(error, "We couldn't load gigs right now. Please try again.")}
              </p>
              <button
                className="mt-6 text-blue-400 hover:text-blue-300 text-[14px] font-medium transition-colors"
                onClick={() => refetch()}
              >
                Try again
              </button>
            </div>
          )}

          {/* Empty state */}
          {!isLoading && !isFetching && !isError && displayedGigs.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-20 h-20 rounded-2xl bg-zinc-900 flex items-center justify-center text-zinc-600 mb-6">
                <FiBriefcase size={40} />
              </div>
              <h3 className="text-white text-xl font-semibold mb-2">No gigs found</h3>
              <p className="text-zinc-500 text-[15px] max-w-xs">
                {activeFilterCount > 0 || debouncedSearch
                  ? 'Try adjusting your filters or search terms'
                  : 'No gigs are available right now. Check back soon!'}
              </p>
              {(activeFilterCount > 0 || debouncedSearch) && (
                <button
                  className="mt-6 text-blue-400 hover:text-blue-300 text-[14px] font-medium transition-colors"
                  onClick={() => {
                    setSearch('');
                    setCity('');
                    setStartDate('');
                    setEndDate('');
                    handleFilterChange();
                  }}
                >
                  Clear all filters
                </button>
              )}
            </div>
          )}

          {/* Gig grid */}
          {displayedGigs.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {displayedGigs.map((gig) => {
                const isOwn =
                  isAuthenticated &&
                  !!(user?.id && gig.owner_id && String(user.id) === String(gig.owner_id));
                return (
                  <GigCard
                    key={gig.id}
                    gig={gig}
                    onViewDetails={(g) =>
                      navigate({
                        to: '/gigs/$gigId',
                        params: { gigId: g.gig_uuid || g.id },
                        search: { apply: false },
                      })
                    }
                    onApply={(g) => {
                      if (!isAuthenticated) {
                        const targetGigId = g.gig_uuid || g.id;
                        navigate({
                          to: '/sign-in',
                          search: { redirect: `/gigs/${targetGigId}?apply=1` },
                        });
                        return;
                      }
                      setApplyGig(g);
                    }}
                    isOwner={isOwn}
                    onEdit={(g) =>
                      navigate({
                        to: '/gigs/$gigId/edit',
                        params: { gigId: g.gig_uuid || g.id },
                        search: { apply: false },
                      })
                    }
                  />
                );
              })}
            </div>
          )}

          {/* Load more */}
          {hasMore && allGigs.length > 0 && (
            <div className="flex justify-center mt-12">
              <button
                className="px-12 py-3.5 rounded-full border border-white/25 text-white text-[14px] font-medium hover:bg-white/5 hover:border-white/40 transition-all active:scale-95 disabled:opacity-40"
                disabled={isFetching}
                onClick={() => setPage((prev) => prev + 1)}
              >
                {isFetching ? 'Loading...' : 'Load more'}
              </button>
            </div>
          )}

          {/* Load more skeleton rows */}
          {isFetching && allGigs.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 mt-5">
              {Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-[18px] border border-white/10 bg-[#0d0d0d] animate-pulse overflow-hidden"
                >
                  <div className="w-full h-58 bg-zinc-800" />
                  <div className="p-4 flex flex-col gap-3">
                    <div className="h-3 bg-zinc-800 rounded w-2/5" />
                    <div className="h-4 bg-zinc-800 rounded w-3/4" />
                    <div className="h-3 bg-zinc-800 rounded w-full" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      <BottomNav />

      <ApplyGigModal
        isOpen={applyGig !== null}
        onClose={() => setApplyGig(null)}
        gig={applyGig as ApplyGigData | null}
        gigId={applyGig ? applyGig.gig_uuid || applyGig.id : ''}
        user={user}
      />
    </div>
  );
}
